/* Sources — list with type / capability count / origin mix, the New-source
 * door (Connect a system | From JSON), Copy pack, and the per-source ONE-loop
 * editor. Head mirrors Workflows / Journeys. */
import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import {
    useSearchParams
} from 'react-router-dom';
import {
    FilePlus2,
    Loader2,
    Plug,
    Plus
} from 'lucide-react';
import {
    appEngineService
} from '../../api/appEngine.service';
import {
    notify
} from 'shared/api/notifier';
import {
    useAppStudio
} from '../../AppStudioContext';
import CopyPackButton from '../../shared/CopyPackButton';
import DefWorkbench from '../../shared/DefWorkbench';
import InjectDialog from '../../shared/InjectDialog';
import ConnectSourceDialog from './ConnectSourceDialog';
import SourceDetail from './SourceDetail';
import type {
    SourceDoc
} from '../../api/types';

interface SourceMeta {
    type: string;
    caps: number;
    mix: string;
}

function summarize(doc: SourceDoc): SourceMeta {
    const caps = Array.isArray(doc?.capabilities) ? doc.capabilities : [];
    const mix: Record<string, number> = {};
    for (const c of caps) {
        const o = c.origin || 'ingested';
        mix[o] = (mix[o] || 0) + 1;
    }
    const mixStr = Object.entries(mix)
        .map(([o, n]) => `${n} ${o.slice(0, 3)}`)
        .join(' · ');
    return {
        type: doc?.source?.type || '?',
        caps: caps.length,
        mix: mixStr
    };
}

export default function SourcesPage() {
    const {
        tenancy
    } = useAppStudio();
    const [connectOpen, setConnectOpen] = useState(false);
    const [injectOpen, setInjectOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [listVersion, setListVersion] = useState(0);
    const [meta, setMeta] = useState<Record<string, SourceMeta>>({});
    const [search, setSearch] = useSearchParams();
    // After a POST-create / connect the engine returns the MINTED src_ id —
    // open the editor on it straight away (selection lives in ?id=).
    const openCreated = (id?: string) => {
        setListVersion((v) => v + 1);
        if (!id) return;
        const next = new URLSearchParams(search);
        next.set('id', id);
        setSearch(next);
    };

    // ── Async ingest jobs ────────────────────────────────────────────────
    // Connect is non-blocking: the dialog enqueues a background job and closes.
    // SourcesPage (which stays mounted while the dialog unmounts) owns polling
    // each job to done/failed — so the work survives the dialog closing. The
    // drafted source is saved server-side regardless, so even if THIS page
    // unmounts the source still appears on the next list load; the poll just
    // adds the live toast + auto-select-when-ready.
    const [jobs, setJobs] = useState<{ id: string; name: string }[]>([]);
    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    const pollJob = async (jobId: string, name: string) => {
        const started = Date.now();
        const drop = () => setJobs((xs) => xs.filter((x) => x.id !== jobId));
        while (Date.now() - started < 180000) {          // 3-min ceiling
            await new Promise((r) => setTimeout(r, 1600));
            if (!aliveRef.current) return;                 // left the page — result is still saved
            let j: any;
            try {
                j = await appEngineService.getConnectJob(tenancy, jobId);
            } catch {
                continue;                                  // transient — keep polling
            }
            if (j?.status === 'done') {
                drop();
                const n = j.capabilities;
                notify(`${name} connected — ${n} capabilit${n === 1 ? 'y' : 'ies'}`, 'success');
                openCreated(j.source_id);
                return;
            }
            if (j?.status === 'failed') {
                drop();
                notify(
                    j.errors && j.errors.length ? j.errors.join('\n') : `${name} failed to connect`,
                    'danger');
                return;
            }
        }
        drop();
        notify(`${name} is still ingesting — it will appear when ready`, 'warning');
        setListVersion((v) => v + 1);
    };

    const startJob = (jobId: string, name: string) => {
        setJobs((xs) => [...xs, { id: jobId, name }]);
        void pollJob(jobId, name);
    };

    // Enrich list rows with type / capability count / origin mix. The list
    // endpoint only returns ids+names, so fetch the (few) source docs.
    useEffect(() => {
        let alive = true;
        appEngineService.listDefs(tenancy, 'source')
            .then((items) => Promise.all(items.map(async (it) => {
                try {
                    const doc = await appEngineService.getDef(tenancy, 'source', it.id);
                    return [it.id, summarize(doc)] as [string, SourceMeta];
                } catch {
                    return null;
                }
            })))
            .then((pairs) => {
                if (!alive) return;
                const m: Record<string, SourceMeta> = {};
                for (const p of pairs) {
                    if (p) m[p[0]] = p[1];
                }
                setMeta(m);
            })
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [tenancy, listVersion]);

    return (
        <>
            <DefWorkbench
                type="source"
                title="Sources"
                subtitle="Connected systems and their capability catalogs"
                emptyHint="Connect a system, or open the editor on the predefined identity source."
                listVersion={listVersion}
                headActions={(
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6
                    }}>
                        <div style={{
                            position: 'relative'
                        }}>
                            <button className="aps-btn aps-btn-primary aps-btn-sm" style={{
                                width: '100%'
                            }} onClick={() => setMenuOpen((v) => !v)}>
                                <Plus size={14} />
                                New source
                            </button>
                            {menuOpen && (
                                <>
                                    <div onClick={() => setMenuOpen(false)} style={{
                                        position: 'fixed',
                                        inset: 0,
                                        zIndex: 40
                                    }} />
                                    <div style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 4px)',
                                        left: 0,
                                        right: 0,
                                        zIndex: 41,
                                        background: 'var(--aps-surface)',
                                        border: '1px solid var(--aps-border)',
                                        borderRadius: '8px',
                                        boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                                        padding: 4,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2
                                    }}>
                                        <button className="aps-rail-link" style={{
                                            fontSize: 'var(--aps-text-sm)'
                                        }} onClick={() => {
                                            setMenuOpen(false);
                                            setConnectOpen(true);
                                        }}>
                                            <Plug size={15} />
                                            <span>Connect a system</span>
                                        </button>
                                        <button className="aps-rail-link" style={{
                                            fontSize: 'var(--aps-text-sm)'
                                        }} onClick={() => {
                                            setMenuOpen(false);
                                            setInjectOpen(true);
                                        }}>
                                            <FilePlus2 size={15} />
                                            <span>From JSON</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                        <CopyPackButton stage="sources" block />
                    </div>
                )}
                itemMeta={(it) => {
                    const m = meta[it.id];
                    if (!m) return it.id;
                    return `${m.type} · ${m.caps} caps${m.mix ? ` · ${m.mix}` : ''}`;
                }}
                renderDetail={(id) => (
                    <SourceDetail
                        id={id}
                        onChanged={() => setListVersion((v) => v + 1)}
                    />
                )}
            />
            <ConnectSourceDialog
                open={connectOpen}
                onOpenChange={setConnectOpen}
                onJobStarted={startJob}
            />
            <InjectDialog
                type="source"
                open={injectOpen}
                onOpenChange={setInjectOpen}
                onCreated={(id) => {
                    setInjectOpen(false);
                    openCreated(id);
                }}
            />
            {jobs.length > 0 && (
                <div style={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    zIndex: 60,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6
                }}>
                    {jobs.map((jb) => (
                        <div key={jb.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            background: 'var(--aps-surface)',
                            border: '1px solid var(--aps-border)',
                            borderRadius: 8,
                            padding: '8px 12px',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                            fontSize: 'var(--aps-text-xs)',
                            color: 'var(--aps-text)'
                        }}>
                            <Loader2 size={13} className="aps-spin"
                                color="var(--aps-primary)" />
                            <span>Ingesting <b>{jb.name}</b>…</span>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
